import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  Menu,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

const TOGGLEABLE_COLUMNS = [
  { field: 'user_id', header: 'User ID' },
  { field: 'first_name', header: 'First Name' },
  { field: 'middle_name', header: 'Middle Name' },
  { field: 'last_name', header: 'Last Name' },
  { field: 'email', header: 'Email' },
  { field: 'is_admin', header: 'Admin' },
  { field: 'employee_id', header: 'EMP ID' },
  { field: 'category', header: 'Category' },
  { field: 'user_type', header: 'User Type' },
  { field: 'work_status', header: 'Work Status' },
  { field: 'department', header: 'Department' },
  { field: 'sub_department', header: 'Sub Department' },
  { field: 'designation', header: 'Designation' },
  { field: 'gender', header: 'Gender' },
  { field: 'blood_group', header: 'Blood Group' },
  { field: 'reporting_manager_id', header: 'Reporting Manager' },
  { field: 'primary_contact', header: 'Primary Contact' },
  { field: 'office_contact', header: 'Office Contact' },
  { field: 'date_of_joining', header: 'Date of Joining' },
  { field: 'date_of_birth', header: 'Date of Birth' },
  { field: 'date_of_leaving', header: 'Date of Leaving' },
  { field: 'notice_period_end_date', header: 'Notice Period End' },
  { field: 'probation_end_date', header: 'Probation End' },
  { field: 'aadhar_number', header: 'Aadhar Number' },
  { field: 'pan_number', header: 'PAN Number' },
  { field: 'permanent_address', header: 'Permanent Address' },
  { field: 'temporary_address', header: 'Temporary Address' },
  { field: 'short_bio', header: 'Short Bio' },
  { field: 'is_account_active', header: 'Account' },
]

/**
 * UserFilters
 * Props:
 *   filters - current filter state
 *   onFilterChange - (patch) => void
 *   listConfigs - { category, user_type, work_status, ... } from getAllCompanyListConfigs
 *   columnVisibilityModel - {}
 *   onColumnVisibilityModelChange - (model) => void
 */
const UserFilters = ({
  filters,
  onFilterChange,
  listConfigs,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search || '')
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null)
  const columnsOpen = Boolean(columnsAnchorEl)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFilterChange({ search: searchInput, page: 1 })
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput]) // eslint-disable-line

  const categoryItems = listConfigs?.category?.items || []
  const userTypeItems = listConfigs?.user_type?.items || []
  const workStatusItems = listConfigs?.work_status?.items || []

  const handleClear = useCallback(() => {
    setSearchInput('')
    onFilterChange({
      search: '',
      category: null,
      user_type: null,
      work_status: null,
      is_account_active: '',
      page: 1,
    })
  }, [onFilterChange])

  const hasActiveFilters =
    filters.search ||
    filters.category ||
    filters.user_type ||
    filters.work_status ||
    filters.is_account_active

  const handleColumnToggle = (field) => {
    onColumnVisibilityModelChange({
      ...columnVisibilityModel,
      [field]: !(columnVisibilityModel?.[field] !== false),
    })
  }

  const handleSelectAllColumns = () => {
    const nextModel = { ...columnVisibilityModel }
    TOGGLEABLE_COLUMNS.forEach((col) => {
      nextModel[col.field] = true
    })
    onColumnVisibilityModelChange(nextModel)
  }

  const handleDeselectAllColumns = () => {
    const nextModel = { ...columnVisibilityModel }
    TOGGLEABLE_COLUMNS.forEach((col) => {
      nextModel[col.field] = false
    })
    onColumnVisibilityModelChange(nextModel)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'center',
        mb: 1,
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name, email, employee ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Autocomplete
          size="small"
          options={categoryItems}
          getOptionLabel={(o) => o.value || ''}
          value={categoryItems.find((o) => o.id === filters.category) || null}
          onChange={(_, v) => onFilterChange({ category: v?.id || null, user_type: null, page: 1 })}
          sx={{ minWidth: 120 }}
          renderInput={(params) => <TextField {...params} label="Category" />}
        />

        <Autocomplete
          size="small"
          options={userTypeItems}
          getOptionLabel={(o) => o.value || ''}
          value={userTypeItems.find((o) => o.id === filters.user_type) || null}
          onChange={(_, v) => onFilterChange({ user_type: v?.id || null, page: 1 })}
          sx={{ minWidth: 120 }}
          renderInput={(params) => <TextField {...params} label="User Type" />}
        />

        <Autocomplete
          size="small"
          options={workStatusItems}
          getOptionLabel={(o) => o.value || ''}
          value={workStatusItems.find((o) => o.id === filters.work_status) || null}
          onChange={(_, v) => onFilterChange({ work_status: v?.id || null, page: 1 })}
          sx={{ minWidth: 120 }}
          renderInput={(params) => <TextField {...params} label="Work Status" />}
        />

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Account</InputLabel>
          <Select
            label="Account"
            value={filters.is_account_active ?? ''}
            onChange={(e) => onFilterChange({ is_account_active: e.target.value, page: 1 })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Chip
            label="Clear filters"
            size="small"
            onDelete={handleClear}
            deleteIcon={<ClearIcon />}
            color="default"
            variant="outlined"
          />
        )}
      </div>
      <>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon />}
          onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
        >
          Columns
        </Button>

        <Menu
          anchorEl={columnsAnchorEl}
          open={columnsOpen}
          onClose={() => setColumnsAnchorEl(null)}
          PaperProps={{ sx: { maxHeight: 420 } }}
        >
          <MenuItem onClick={handleSelectAllColumns}>
            <Typography variant="body2" fontWeight={600}>
              Select All
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleDeselectAllColumns}>
            <Typography variant="body2" fontWeight={600}>
              Deselect All
            </Typography>
          </MenuItem>
          {TOGGLEABLE_COLUMNS.map((col) => {
            const checked = columnVisibilityModel?.[col.field] !== false
            return (
              <MenuItem key={col.field} onClick={() => handleColumnToggle(col.field)}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={checked} />}
                  label={<Typography variant="body2">{col.header}</Typography>}
                />
              </MenuItem>
            )
          })}
        </Menu>
      </>
    </Box>
  )
}

export default UserFilters
